import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Paper,
  Box,
  Tabs,
  Tab,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import MastercardAnywhereDetail from "./MastercardAnywhereDetail";
import ProviderEmailDialog from "../components/ProviderEmailDialog";
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useNotify } from "react-admin";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import WarningIcon from "@material-ui/icons/Warning";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import PeopleIcon from "@material-ui/icons/People";
import {
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Line,
  ComposedChart,
  Legend,
} from "recharts";
import CardWithIcon from "../dashboard/CardWithIcon";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

// Change this to switch the status the dashboard lands on. "" = All.
const DEFAULT_STATUS_FILTER = "ordered";

const STATUS_OPTIONS = [
  "",
  "pending_payment",
  "payment_failed",
  "ordering",
  "ordered",
  "activated",
  "expired",
  "failed",
  "swept",
  "cancelled",
  "archived",
];


const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending_payment: { bg: "#fff3e0", color: "#e65100" },
  payment_failed: { bg: "#fce4ec", color: "#c62828" },
  ordering: { bg: "#e3f2fd", color: "#1565c0" },
  ordered: { bg: "#e8f5e9", color: "#2e7d32" },
  activated: { bg: "#e8f5e9", color: "#1b5e20" },
  expired: { bg: "#efebe9", color: "#4e342e" },
  failed: { bg: "#fce4ec", color: "#b71c1c" },
  swept: { bg: "#f3e5f5", color: "#6a1b9a" },
  cancelled: { bg: "#ffebee", color: "#c62828" },
  archived: { bg: "#eceff1", color: "#455a64" },
};

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "1em",
  },
  section: {
    width: "100%",
    maxWidth: 1400,
    margin: "0.5em auto",
  },
  statsRow: {
    display: "flex",
    gap: "1em",
    flexWrap: "wrap" as any,
    marginBottom: "1em",
  },
  chartPaper: {
    padding: "1em",
    marginBottom: "1em",
  },
  filterRow: {
    display: "flex",
    gap: "1em",
    alignItems: "flex-end",
    flexWrap: "wrap" as any,
    marginBottom: "1em",
  },
  tableCell: {
    padding: "6px 10px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
  },
  tableCellHead: {
    padding: "6px 10px",
    border: "1px solid #ddd",
    fontWeight: 700,
    fontSize: "0.85rem",
    backgroundColor: "#f5f5f5",
  },
  paginationRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "1em",
    gap: "1em",
  },
  funnelRow: {
    display: "flex",
    gap: "0.5em",
    alignItems: "stretch",
    marginBottom: "1em",
  },
  funnelStep: {
    flex: 1,
    padding: "0.75em 1em",
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    minWidth: 0,
  },
  funnelStepLabel: {
    fontSize: "0.7rem",
    color: "#666",
    textTransform: "uppercase" as any,
    letterSpacing: 0.5,
    fontWeight: 600,
  },
  funnelStepValue: {
    fontSize: "1.6rem",
    fontWeight: 700,
    color: "#222",
    lineHeight: 1.1,
  },
  funnelStepRate: {
    fontSize: "0.7rem",
    color: "#888",
    marginTop: 4,
  },
  funnelArrow: {
    display: "flex",
    alignItems: "center",
    color: "#999",
    fontSize: "1.4em",
    padding: "0 0.25em",
  },
  alertBanner: {
    background: "#fff3e0",
    border: "1px solid #ffb74d",
    borderRadius: 6,
    padding: "0.75em 1em",
    marginBottom: "1em",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75em",
  },
  alertList: {
    display: "flex",
    flexWrap: "wrap" as any,
    gap: "0.75em",
    marginTop: "0.5em",
  },
  alertChip: {
    background: "white",
    border: "1px solid #ffb74d",
    padding: "0.25em 0.75em",
    borderRadius: 4,
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "1em",
    marginBottom: "1em",
  },
  metricCard: {
    padding: "1em",
    background: "white",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
  },
  metricCardTitle: {
    fontSize: "0.75rem",
    textTransform: "uppercase" as any,
    color: "#666",
    fontWeight: 700,
    letterSpacing: 0.5,
    marginBottom: "0.5em",
  },
  metricRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0.25em 0",
    fontSize: "0.9rem",
  },
  metricLabel: { color: "#666" },
  metricValue: { fontWeight: 600, color: "#222" },
  highlight: {
    color: "#DA2972",
    fontWeight: 700,
  },
}));

const MastercardAnywhereDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Stats
  const [stats, setStats] = useState<any>(null);

  // Cards list
  const [cards, setCards] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });

  // Filters
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(DEFAULT_STATUS_FILTER);
  const [fromDate, setFromDate] = useState("");
  // Selected card for the in-place detail Dialog. null = closed.
  // Using a Dialog instead of route navigation preserves pagination state and
  // the position the admin was viewing — see SP-72 follow-up.
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [emailCard, setEmailCard] = useState<any | null>(null);
  const [emailBodyBefore, setEmailBodyBefore] = useState("");
  const [emailBodyAfter, setEmailBodyAfter] = useState("");
  const [emailReason, setEmailReason] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailingCardId, setEmailingCardId] = useState<number | null>(null);
  const [toDate, setToDate] = useState("");


  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ADMIN,
        { action: "stats" },
        { headers: getConfigPost() }
      );
      if (res.data?.status) setStats(res.data.data);
    } catch (err) {
      notify("Failed to load stats", "warning");
    } finally {
      setStatsLoading(false);
    }
  };

  // Overrides exist because setState has not applied yet when Clear re-fetches.
  const fetchCards = async (page = 1, overrides?: { email?: string; status?: string; fromDate?: string; toDate?: string }) => {
    try {
      setLoading(true);
      const active = { email, status, fromDate, toDate, ...overrides };
      const params: any = { page, limit: 20 };
      if (active.email) params.email = active.email;
      if (active.status) params.status = active.status;
      if (active.fromDate) params.from_date = active.fromDate;
      if (active.toDate) params.to_date = active.toDate;

      const res: any = await axios.get(ENDPOINTS.MC_ANYWHERE_ADMIN, getConfigGet(params));
      if (res.data?.status) {
        setCards(res.data.data.cards || []);
        setPagination(res.data.data.pagination || { page: 1, limit: 20, total: 0, total_pages: 0 });
      }
    } catch (err) {
      notify("Failed to load cards", "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCards();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    fetchCards(1);
  };

  const handleClear = () => {
    setEmail("");
    setStatus(DEFAULT_STATUS_FILTER);
    setFromDate("");
    setToDate("");
    fetchCards(1, { email: "", status: DEFAULT_STATUS_FILTER, fromDate: "", toDate: "" });
  };

  const openNeocurrencyEmail = async (card: any) => {
    setEmailingCardId(card.id);
    try {
      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ADMIN,
        { action: "email_neocurrency", card_id: card.id },
        { headers: getConfigPost() }
      );
      setEmailBodyBefore(res.data?.email_body_before || "");
      setEmailBodyAfter(res.data?.email_body_after || "");
      setEmailReason("");
      setEmailCard(card);
    } catch (err: any) {
      notify(err?.response?.data?.message || "Failed to load email draft", "warning");
    } finally {
      setEmailingCardId(null);
    }
  };

  const closeNeocurrencyEmail = () => {
    setEmailCard(null);
    setEmailBodyBefore("");
    setEmailBodyAfter("");
    setEmailReason("");
  };

  const sendNeocurrencyEmail = async () => {
    if (!emailCard) return;
    setEmailSending(true);
    try {
      const reasonLine = emailReason.trim() ? `\n\nReason: ${emailReason.trim()}` : "";
      const fullBody = `${emailBodyBefore}${reasonLine}\n\n${emailBodyAfter}`;

      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ADMIN,
        { action: "email_neocurrency", card_id: emailCard.id, body: fullBody },
        { headers: getConfigPost() }
      );
      notify(res.data?.message || "Email sent to NeoCurrency", res.data?.status ? "info" : "warning");
      closeNeocurrencyEmail();
    } catch (err: any) {
      notify(err?.response?.data?.message || "Failed to email NeoCurrency", "warning");
    } finally {
      setEmailSending(false);
    }
  };

  const renderStatusChip = (s: string) => {
    const colors = STATUS_COLORS[s] || { bg: "#f5f5f5", color: "#333" };
    return <Chip label={s} size="small" style={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }} />;
  };

  const summary = stats?.summary || {};
  const revenue = stats?.revenue || {};
  const analysis = stats?.analysis || {};
  const headline = analysis.headline || {};
  const funnel = analysis.funnel || { rates: {} };
  const payment = analysis.payment || { by_provider: [] };
  const cohort = analysis.cohort || { grant_to_first_card_hours: {} };
  const sweepImpact = analysis.sweep_impact || { untapped_potential: {} };
  const usage = analysis.usage || {};
  const denominations: any[] = analysis.denominations || [];
  const topDenominations = [...denominations]
    .sort((a: any, b: any) => Number(b.n || 0) - Number(a.n || 0))
    .slice(0, 15);
  const denomMaxN = topDenominations.reduce((m: number, d: any) => Math.max(m, Number(d.n || 0)), 0);
  const repeatPurchase = analysis.repeat_purchase || {};
  const health = analysis.health || {};
  const integrity = analysis.integrity || {};

  const pct = (v: number | undefined) =>
    v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`;
  const gbp = (v: number | string | undefined, dp = 2) =>
    v == null || isNaN(Number(v)) ? "—" : `£${Number(v).toFixed(dp)}`;

  // Build alert list (only non-zero items)
  const alertItems: Array<{ key: string; label: string; value: number }> = [
    { key: "stuck_pending_payment_over_1h", label: "Stuck pending payment > 1h", value: Number(health.stuck_pending_payment_over_1h || 0) },
    { key: "stuck_ordering_over_30m", label: "Stuck ordering > 30m", value: Number(health.stuck_ordering_over_30m || 0) },
    { key: "ordered_over_7d_no_activation", label: "Ordered > 7d, no activation", value: Number(health.ordered_over_7d_no_activation || 0) },
    { key: "cashback_uncredited_over_24h", label: "Cashback uncredited > 24h", value: Number(health.cashback_uncredited_over_24h || 0) },
    { key: "sweep_eligible_unswept", label: "Sweep-eligible, unswept", value: Number(health.sweep_eligible_unswept || 0) },
    { key: "expiring_next_30d_with_balance", label: "Expiring 30d w/ balance", value: Number(health.expiring_next_30d_with_balance || 0) },
  ].filter((a) => a.value > 0);

  const integrityIssues: Array<{ key: string; label: string; value: number }> = [
    { key: "null_wholesale_cost_rows", label: "Null wholesale_cost rows", value: Number(integrity.null_wholesale_cost_rows || 0) },
    { key: "face_value_wholesale_cost_rows", label: "Cost = face value (lookup failed)", value: Number(integrity.face_value_wholesale_cost_rows || 0) },
    { key: "refunded_in_active_states", label: "Refunded card in counted states", value: Number(integrity.refunded_in_active_states || 0) },
    { key: "uncredited_cashback_active", label: "Active card with uncredited cashback", value: Number(integrity.uncredited_cashback_active || 0) },
  ].filter((a) => a.value > 0);

  return (
    <div className={classes.main}>
      <div className={classes.section}>
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: "0.5em" }}>
          Mastercard Anywhere
        </Typography>

        {/* Health alert banner — only when alerts > 0 */}
        {!statsLoading && alertItems.length > 0 && (
          <div className={classes.alertBanner}>
            <WarningIcon style={{ color: "#ef6c00" }} />
            <Box flex={1}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#e65100" }}>
                {health.alert_count} health item{health.alert_count === 1 ? "" : "s"} need attention
              </Typography>
              <div className={classes.alertList}>
                {alertItems.map((a) => (
                  <span key={a.key} className={classes.alertChip}>
                    {a.label}: <span style={{ color: "#c62828" }}>{a.value}</span>
                  </span>
                ))}
              </div>
            </Box>
          </div>
        )}

        {/* Headline strip */}
        {!statsLoading && stats && (
          <div className={classes.statsRow}>
            <CardWithIcon
              icon={undefined as any}
              title={`Net Revenue${headline.delta_7d ? ` (7d Δ ${gbp(headline.delta_7d.net_revenue)})` : ""}`}
              subtitle={gbp(headline.net_revenue_realised ?? revenue.net_revenue)}
              to=""
            />
            <CardWithIcon
              icon={TrendingUpIcon}
              title="Margin %"
              subtitle={headline.margin_pct != null ? `${headline.margin_pct}%` : "—"}
              to=""
            />
            <CardWithIcon
              icon={CreditCardIcon}
              title={`Cards sold${headline.delta_7d ? ` (7d: +${headline.delta_7d.cards})` : ""}`}
              subtitle={headline.cards_counted || summary.total_cards || 0}
              to=""
            />
            <CardWithIcon
              icon={CheckCircleIcon}
              title={`Payment success${payment.last_7d_success_rate != null ? ` (7d ${pct(payment.last_7d_success_rate)})` : ""}`}
              subtitle={pct(payment.lifetime_success_rate)}
              to=""
            />
            <CardWithIcon
              icon={AccountBalanceIcon}
              title={`To mortgage${sweepImpact.untapped_potential?.total_leftover_balance ? ` (untapped ${gbp(sweepImpact.untapped_potential.total_leftover_balance, 0)})` : ""}`}
              subtitle={gbp(sweepImpact.total_swept_to_mortgage)}
              to=""
            />
            <CardWithIcon
              icon={PeopleIcon}
              title={`Granted → card (${cohort.granted_with_card || 0}/${cohort.granted_total || 0})`}
              subtitle={pct(cohort.conversion_rate)}
              to=""
            />
          </div>
        )}

        {/* Conversion funnel */}
        {!statsLoading && analysis.funnel && (
          <Paper className={classes.chartPaper}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: "0.75em" }}>
              Conversion funnel
            </Typography>
            <div className={classes.funnelRow}>
              {[
                { label: "Granted", value: funnel.granted, rate: null as string | null },
                { label: "Paid", value: funnel.paid, rate: funnel.granted ? `${pct(funnel.rates?.granted_to_card)} have a card` : null },
                { label: "Ordered", value: funnel.ordered, rate: pct(funnel.rates?.paid_to_ordered) + " of paid" },
                { label: "Activated", value: funnel.activated, rate: pct(funnel.rates?.ordered_to_activated) + " of ordered" },
                { label: "Used", value: funnel.used, rate: pct(funnel.rates?.activated_to_used) + " of activated" },
                { label: "Swept", value: funnel.swept, rate: pct(funnel.rates?.activated_to_swept) + " of activated" },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  {i > 0 && <div className={classes.funnelArrow}>›</div>}
                  <div className={classes.funnelStep}>
                    <div className={classes.funnelStepLabel}>{step.label}</div>
                    <div className={classes.funnelStepValue}>{Number(step.value || 0).toLocaleString()}</div>
                    {step.rate && <div className={classes.funnelStepRate}>{step.rate}</div>}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </Paper>
        )}

        {/* Cohort / Sweep impact / Repeat purchase / Usage cards */}
        {!statsLoading && stats && (
          <div className={classes.metricGrid}>
            <div className={classes.metricCard}>
              <div className={classes.metricCardTitle}>Granted-user cohort</div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Total granted</span><span className={classes.metricValue}>{Number(cohort.granted_total || 0).toLocaleString()}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>With any card</span><span className={classes.metricValue}>{cohort.granted_with_card || 0} ({pct(cohort.conversion_rate)})</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Revenue from cohort</span><span className={classes.metricValue}>{gbp(cohort.revenue_from_granted)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>£ per granted user</span><span className={classes.metricValue}>{gbp(cohort.revenue_per_granted_user, 4)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>£ per converted</span><span className={classes.metricValue}>{gbp(cohort.revenue_per_converted)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Avg grant → 1st card</span><span className={classes.metricValue}>{cohort.grant_to_first_card_hours?.avg ? `${cohort.grant_to_first_card_hours.avg}h` : "—"}</span></div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricCardTitle}>Sweep impact (mortgage)</div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Cards swept</span><span className={classes.metricValue}>{sweepImpact.swept_cards || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Swept to mortgage</span><span className={classes.metricValue}>{gbp(sweepImpact.total_swept_to_mortgage)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Avg sweep</span><span className={classes.metricValue}>{gbp(sweepImpact.avg_sweep_amount)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Sweep-eligible, unswept</span><span className={classes.metricValue} style={{ color: (sweepImpact.sweep_eligible_unswept || 0) > 0 ? "#c62828" : undefined }}>{sweepImpact.sweep_eligible_unswept || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Cards w/ leftover</span><span className={classes.metricValue}>{sweepImpact.untapped_potential?.active_cards_with_balance || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Untapped £</span><span className={classes.highlight}>{gbp(sweepImpact.untapped_potential?.total_leftover_balance)}</span></div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricCardTitle}>Usage</div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Activated cards</span><span className={classes.metricValue}>{usage.activated_cards || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Unspent</span><span className={classes.metricValue}>{usage.cards_unspent || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Partially spent</span><span className={classes.metricValue}>{usage.cards_partially_spent || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Fully spent</span><span className={classes.metricValue}>{usage.cards_fully_spent || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Avg spent / used card</span><span className={classes.metricValue}>{gbp(usage.avg_spent_per_used)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Avg card lifetime</span><span className={classes.metricValue}>{usage.avg_card_lifetime_days != null ? `${usage.avg_card_lifetime_days} days` : "—"}</span></div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricCardTitle}>Repeat purchase</div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Buyers</span><span className={classes.metricValue}>{repeatPurchase.users_with_any_card || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Repeat buyers</span><span className={classes.metricValue}>{repeatPurchase.repeat_buyers || 0} ({pct(repeatPurchase.repeat_rate)})</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Avg cards/buyer</span><span className={classes.metricValue}>{repeatPurchase.avg_cards_per_buyer || 0}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Max cards (single user)</span><span className={classes.metricValue}>{repeatPurchase.max_cards_per_user || 0}</span></div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricCardTitle}>Revenue breakdown</div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Gross face value</span><span className={classes.metricValue}>{gbp(headline.gross_face_value)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Gross margin</span><span className={classes.metricValue}>{gbp(headline.gross_margin)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Margin %</span><span className={classes.metricValue}>{headline.margin_pct != null ? `${headline.margin_pct}%` : "—"}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Net revenue (realised)</span><span className={classes.metricValue}>{gbp(headline.net_revenue_realised)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>Net revenue (accrued)</span><span className={classes.metricValue}>{gbp(headline.net_revenue_accrued)}</span></div>
              <div className={classes.metricRow}><span className={classes.metricLabel}>30d revenue Δ</span><span className={classes.metricValue}>{gbp(headline.delta_30d?.net_revenue)}</span></div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricCardTitle}>Payment by provider</div>
              {(payment.by_provider || []).length === 0 && <div className={classes.metricRow}><span className={classes.metricLabel}>No data</span></div>}
              {(payment.by_provider || []).map((p: any) => (
                <div key={p.provider}>
                  <div className={classes.metricRow}><span className={classes.metricLabel} style={{ fontWeight: 600, textTransform: "capitalize" }}>{p.provider}</span><span className={classes.metricValue} style={{ color: p.success_rate < 0.8 ? "#c62828" : undefined }}>{pct(p.success_rate)}</span></div>
                  <div className={classes.metricRow} style={{ fontSize: "0.8rem" }}><span className={classes.metricLabel}>  attempts / failed</span><span className={classes.metricValue}>{p.attempts} / {p.failed}</span></div>
                </div>
              ))}
              {payment.worst_day_last_30d && (
                <div className={classes.metricRow}><span className={classes.metricLabel}>Worst day (30d)</span><span className={classes.metricValue}>{new Date(payment.worst_day_last_30d.date).toLocaleDateString()} — {pct(payment.worst_day_last_30d.success_rate)}</span></div>
              )}
            </div>
          </div>
        )}

        {/* Integrity issues — quieter section, only if any */}
        {!statsLoading && integrityIssues.length > 0 && (
          <Paper className={classes.chartPaper} style={{ borderLeft: "4px solid #c62828" }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: "0.5em", color: "#c62828" }}>
              Data integrity warnings
            </Typography>
            <div className={classes.alertList}>
              {integrityIssues.map((a) => (
                <span key={a.key} className={classes.alertChip} style={{ borderColor: "#c62828" }}>
                  {a.label}: <span style={{ color: "#c62828" }}>{a.value}</span>
                </span>
              ))}
            </div>
          </Paper>
        )}

        {/* Top denominations (top 15 by volume) */}
        {topDenominations.length > 0 && (
          <Paper className={classes.chartPaper}>
            <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Top denominations by volume
              </Typography>
              <Typography variant="caption" style={{ color: "#888" }}>
                showing top {topDenominations.length} of {denominations.length}
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Denom", "Sold", "Share", "Avg margin", "Margin %", "Swept"].map((h) => (
                    <TableCell key={h} className={classes.tableCellHead}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {topDenominations.map((d: any) => {
                  const sharePct = denomMaxN > 0 ? (Number(d.n || 0) / denomMaxN) * 100 : 0;
                  return (
                    <TableRow key={d.denomination}>
                      <TableCell className={classes.tableCell} style={{ fontWeight: 600 }}>
                        {gbp(d.denomination, 0)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>{Number(d.n || 0).toLocaleString()}</TableCell>
                      <TableCell className={classes.tableCell} style={{ minWidth: 140 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <Box
                            flex={1}
                            height={8}
                            borderRadius={4}
                            style={{ background: "#f0f0f0", overflow: "hidden" }}
                          >
                            <Box
                              height="100%"
                              width={`${Math.max(2, sharePct)}%`}
                              style={{ background: "#DA2972" }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tableCell}>{gbp(d.avg_margin)}</TableCell>
                      <TableCell className={classes.tableCell}>
                        {d.margin_pct != null ? `${d.margin_pct}%` : "—"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {d.swept_count > 0
                          ? `${d.swept_count} (${d.sweep_rate_pct != null ? d.sweep_rate_pct + "%" : "—"})`
                          : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* Daily trend chart — enriched with payment success line */}
        {stats?.daily_orders?.length > 0 && (
          <Paper className={classes.chartPaper}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: "0.5em" }}>
              Daily orders + payment success — last 30 days
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={[...stats.daily_orders].reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    if (name === "Payment success") return [pct(Number(value)), name];
                    if (name === "Net revenue") return [gbp(Number(value)), name];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="paid" stackId="a" fill="#2e7d32" name="Paid" />
                <Bar yAxisId="left" dataKey="payment_failed" stackId="a" fill="#c62828" name="Payment failed" />
                <Line yAxisId="right" type="monotone" dataKey="payment_success_rate" stroke="#DA2972" strokeWidth={2} dot={false} name="Payment success" />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        )}


        {/* Tabs */}
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} indicatorColor="primary">
          <Tab label="All Cards" />
          <Tab label="Top Users" />
        </Tabs>

        {/* Cards Tab */}
        {activeTab === 0 && (
          <>
            {/* Filters */}
            <div className={classes.filterRow} style={{ marginTop: "1em" }}>
              <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} size="small" style={{ minWidth: 250 }} />
              <FormControl size="small" style={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select value={status} onChange={(e: any) => setStatus(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.filter(Boolean).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              <TextField label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              <Button variant="contained" color="primary" onClick={handleSearch} size="small">Search</Button>
              <Button variant="outlined" onClick={handleClear} size="small">Clear</Button>
            </div>

            {/* Table */}
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["ID", "User Email", "Amount", "Status", "Cashback", "Balance", "Sweep", "Created", "Actions"].map((h) => (
                        <TableCell key={h} className={classes.tableCellHead}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cards.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className={classes.tableCell} align="center">No cards found</TableCell></TableRow>
                    ) : cards.map((card: any) => (
                      <TableRow key={card.id} hover style={{ cursor: "pointer" }} onClick={() => setSelectedCardId(card.id)}>
                        <TableCell className={classes.tableCell}>{card.id}</TableCell>
                        <TableCell className={classes.tableCell}>{card.email}</TableCell>
                        <TableCell className={classes.tableCell}>£{parseFloat(card.denomination).toFixed(2)}</TableCell>
                        <TableCell className={classes.tableCell}>{renderStatusChip(card.status)}</TableCell>
                        <TableCell className={classes.tableCell}>£{parseFloat(card.cashback_amount || 0).toFixed(2)}</TableCell>
                        <TableCell className={classes.tableCell}>{card.balance != null ? `£${parseFloat(card.balance).toFixed(2)}` : "-"}</TableCell>
                        <TableCell className={classes.tableCell}>{card.sweep_amount ? `£${parseFloat(card.sweep_amount).toFixed(2)}` : "-"}</TableCell>
                        <TableCell className={classes.tableCell}>{card.createdAt?.slice(0, 10).split("-").reverse().join("/")}</TableCell>
                        <TableCell className={classes.tableCell} onClick={(e) => e.stopPropagation()}>
                          <Box display="flex" style={{ gap: 6 }}>
                            <Button size="small" color="primary" variant="outlined" onClick={() => setSelectedCardId(card.id)}>View</Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={emailingCardId === card.id}
                              onClick={() => openNeocurrencyEmail(card)}
                            >
                              {emailingCardId === card.id ? <CircularProgress size={18} /> : "Email NC"}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className={classes.paginationRow}>
                  <IconButton disabled={pagination.page <= 1} onClick={() => fetchCards(pagination.page - 1)}>
                    <NavigateBeforeIcon />
                  </IconButton>
                  <Typography variant="body2">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} cards)
                  </Typography>
                  <IconButton disabled={pagination.page >= pagination.total_pages} onClick={() => fetchCards(pagination.page + 1)}>
                    <NavigateNextIcon />
                  </IconButton>
                </div>
              </>
            )}
          </>
        )}

        {/* Top Users Tab */}
        {activeTab === 1 && (
          <Table size="small" style={{ marginTop: "1em" }}>
            <TableHead>
              <TableRow>
                {["#", "User", "Email", "Cards", "Total Spend"].map((h) => (
                  <TableCell key={h} className={classes.tableCellHead}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(stats?.top_users || []).map((u: any, i: number) => (
                <TableRow key={u.user_id}>
                  <TableCell className={classes.tableCell}>{i + 1}</TableCell>
                  <TableCell className={classes.tableCell}>{u.first_name} {u.last_name}</TableCell>
                  <TableCell className={classes.tableCell}>{u.email}</TableCell>
                  <TableCell className={classes.tableCell}>{u.card_count}</TableCell>
                  <TableCell className={classes.tableCell}>£{parseFloat(u.total_spend).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail Dialog — opened by clicking a row or the View button.
          Keeps dashboard pagination + search filters intact when closed.
          Standalone /mastercardAnywhere/:id route still works for deep links. */}
      <Dialog
        open={selectedCardId != null}
        onClose={() => setSelectedCardId(null)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" style={{ fontWeight: 700 }}>Card #{selectedCardId}</Typography>
            <IconButton size="small" onClick={() => setSelectedCardId(null)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCardId != null && (
            <MastercardAnywhereDetail
              cardIdProp={selectedCardId}
              embedded
              onActionComplete={() => {
                fetchCards(pagination.page);
                fetchStats();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ProviderEmailDialog
        open={!!emailCard}
        onClose={closeNeocurrencyEmail}
        title="Email to NeoCurrency"
        subtitle={`Card #${emailCard?.id || ""} reference and subject are added automatically. Add the reason in the highlighted field below.`}
        bodyBefore={emailBodyBefore}
        onBodyBeforeChange={setEmailBodyBefore}
        bodyAfter={emailBodyAfter}
        onBodyAfterChange={setEmailBodyAfter}
        reason={emailReason}
        onReasonChange={setEmailReason}
        reasonLabel="Reason (Optional)"
        reasonPlaceholder="Enter the reason for contacting NeoCurrency..."
        reasonHelperText={`${emailReason.length}/500`}
        reasonError={false}
        reasonHighlight={false}
        loading={emailSending}
        sendDisabled={false}
        onSend={sendNeocurrencyEmail}
        done={false}
      />

      <Notification />
    </div>
  );
};

export default MastercardAnywhereDashboard;
